import clsx from 'clsx';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './icons';
import './menu.css';

// 下拉菜单 / 浮层：贴着触发元素弹出，点外面或 Esc 关闭。只做定位和开关，内容由调用方决定。

function usePosition(anchor: RefObject<HTMLElement | null>, open: boolean, align: 'start' | 'end', width: number) {
  const [pos, setPos] = useState<{ top: number; left: number; up: boolean }>({ top: 0, left: 0, up: false });
  useLayoutEffect(() => {
    if (!open || !anchor.current) return;
    const place = () => {
      const r = anchor.current!.getBoundingClientRect();
      const up = r.bottom + 320 > window.innerHeight && r.top > 320;
      let left = align === 'start' ? r.left : r.right - width;
      left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
      setPos({ top: up ? r.top - 4 : r.bottom + 4, left, up });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, anchor, align, width]);
  return pos;
}

export function Popover({
  anchor,
  open,
  onClose,
  width = 240,
  align = 'start',
  children,
}: {
  anchor: RefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  width?: number;
  align?: 'start' | 'end';
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const pos = usePosition(anchor, open, align, width);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (ref.current?.contains(t) || anchor.current?.contains(t)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [open, onClose, anchor]);
  if (!open) return null;
  return createPortal(
    <div
      ref={ref}
      className="wd-pop"
      style={{ width, left: pos.left, top: pos.top, transform: pos.up ? 'translateY(-100%)' : undefined }}
      role="dialog"
    >
      {children}
    </div>,
    document.body,
  );
}

export interface MenuItem<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  hint?: ReactNode;
}

/** 可勾选的菜单（筛选、切换状态） */
export function MenuList<T extends string>({
  items,
  selected,
  onPick,
  search,
  emptyText = '没有匹配的选项',
}: {
  items: MenuItem<T>[];
  selected: Set<T> | T | null;
  onPick: (v: T) => void;
  search?: boolean;
  emptyText?: string;
}) {
  const isOn = (v: T) => (selected instanceof Set ? selected.has(v) : selected === v);
  const [q, setQ] = useState('');
  // 单选菜单打开时高亮当前值
  const [hl, setHl] = useState(() => (selected instanceof Set ? 0 : Math.max(0, items.findIndex((it) => it.value === selected))));
  const inputRef = useRef<HTMLInputElement>(null);
  const shown = items.filter((it) => !q.trim() || String(typeof it.label === 'string' ? it.label : it.value).includes(q.trim()));
  useEffect(() => {
    if (search) inputRef.current?.focus();
  }, [search]);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) first.current = false;
    else setHl(0);
  }, [q]);
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHl((h) => Math.min(h + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHl((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && shown[hl]) {
      e.preventDefault();
      onPick(shown[hl].value);
    }
  };
  return (
    <div onKeyDown={onKey}>
      {search && (
        <div className="wd-pop-search">
          <Icon name="search" size={14} />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜索…" aria-label="搜索" />
        </div>
      )}
      <div className="wd-pop-list" role="listbox" tabIndex={search ? -1 : 0}>
        {shown.length === 0 && <div className="wd-pop-empty">{emptyText}</div>}
        {shown.map((it, i) => (
          <button
            key={it.value}
            type="button"
            role="option"
            aria-selected={isOn(it.value)}
            className={clsx('wd-pop-item', i === hl && 'hl')}
            onMouseEnter={() => setHl(i)}
            onClick={() => onPick(it.value)}
          >
            {it.icon}
            <span className="flex1 ellipsis">{it.label}</span>
            {it.hint && <span className="t-meta">{it.hint}</span>}
            <span className="chk">{isOn(it.value) && <Icon name="check" size={14} color="var(--text-2)" />}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
